import { HttpClient, HttpHeaders, HttpStatusCode } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CreateTenantJobDto } from '@app/Models/create-tenant-job.dto';
import { CreateTenantDto } from '@app/Models/create-tenant.dto';
import { NewCloneTenantDto } from '@app/Models/new-clone-tenant.dto';
import { ProvisionedApplicationViewModel } from '@app/Tenant/Applications/Airsweb/Models/provisioned-application.view-model';
import { TenantListPageResult } from '@app/Tenant/Applications/Airsweb/Models/tenant-list-page-result';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { MigrationDto } from '../Migration/Models';
import { FeatureFlag } from '../Models/FeatureFlags';
import { IContinuationResponse } from '../Models/IContinuationResponse';
import { IMobileListItem } from '../Models/IMobileListItem';
import { IModuleQueryDetailsModel } from '../Models/IModuleQueryDetailsModel';
import { INewAttribute } from '../Models/INewAttribute';
import { ListViewModel } from '../Models/ListViewModel';
import {
    MatrixViewModel,
    NamedMatrixViewModel,
} from '../Models/MatrixViewModel';
import { QueryViewModel } from '../Models/QueryViewModel';
import { IRule, Tenant } from '../Models/tenant';
import { TenantGroupPermissionDto } from '../Models/tenant-group-permission.dto';
import { TenantGroupPermission } from '../Models/tenant-group-permission.model';
import { TenantGroupDto } from '../Models/tenant-group.dto';
import { TenantProperty } from '../Models/tenantProperty';
import { AvaModule } from '../Tenant/Applications/Airsweb/Models/AvaModule';
import {
    CopyConfigExportImportRequest,
    CopyConfigExportOnlyRequest,
    CopyConfigImportOnlyRequest
} from '../Tenant/Applications/Airsweb/Models/copy-config-export-request';
import { ListItemViewModel } from '@app/Tenant/Applications/Airsweb/Models/list-item-view-model';
import { PermissionService } from './permission.service';
import { ProvisionedApplicationsTypes } from '@app/Enums';
import { TenantEnvironmentDetails } from '@app/Models/tenant-environment-details.model';
import { TenantAttributes } from "@app/Models/tenant-attributes.model";
import { NewFreeTrialTenantDto } from '@app/Models/new-free-trial-tenant.dto';

@Injectable({
    providedIn: 'root',
})
export class TenantService {
    tenants: Tenant[] = [];
    stateRules: IRule[];
    emailRules: IRule[];
    tenantStatus: BehaviorSubject<string> = new BehaviorSubject('None');
    tenantType: BehaviorSubject<string> = new BehaviorSubject(null);
    tenantPermissionGroup: BehaviorSubject<string> = new BehaviorSubject(null);
    ecoTranslateSynchronisation: BehaviorSubject<boolean> = new BehaviorSubject(
        false
    );
    addedList$: BehaviorSubject<any> = new BehaviorSubject(null);
    updatedListItem$: BehaviorSubject<any> = new BehaviorSubject<any>(null);
    tenantEnvironmentDetails = new BehaviorSubject<TenantEnvironmentDetails>(null);
    organisationId: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
    selectedTenant$ = new BehaviorSubject<Tenant | null>(null);
    tenants$ = new BehaviorSubject<Tenant[]>([]);

    private currentSelectedTenant: Tenant;

    public constructor(
        private http: HttpClient,
        private permissionService: PermissionService
    ) { }

    get selectedTenant(): Tenant {
        return this.currentSelectedTenant;
    }

    set selectedTenant(tenant: Tenant) {
        this.currentSelectedTenant = tenant;
        this.selectedTenant$.next(tenant);
    }

    /** Resets organisationId to null. Called by tenant.resolver before setting selectedTenant
     *  to prevent identifyTenant from passing through a stale org ID. */
    public resetOrganisationId(): void {
        this.organisationId.next(null);
    }

    get selectedTenantHasAirswebApplication(): boolean {
        return (
            this.currentSelectedTenant &&
            this.currentSelectedTenant.ProvisionedApplications.includes(ProvisionedApplicationsTypes.Airsweb)
        );
    }

    getTenants() {
        return this.getAllTenants()
            .toPromise()
            .then((response) => {
                this.tenants = response.filter(tenant => tenant.HasAccess);
                this.tenants$.next(this.tenants);
                return response;
            })
            .catch(this.handleError);
    }

    loadTenants(): Observable<Tenant[]> {
        if (this.tenants.length > 0) {
            return of(this.tenants);
        }
        return this.getAllTenants().pipe(
            map(response => {
                this.tenants = response.filter(tenant => tenant.HasAccess);
                this.tenants$.next(this.tenants);
                return this.tenants;
            })
        );
    }

    loadTenantMetadata(clientId: string): void {
        this.getTenantStatus(clientId);
        this.getTenantType(clientId);
        this.getTenantPermissionGroup(clientId);
        this.getEcoTranslateSynchronisation(clientId);
        this.getTenantOrganisationId();
    }

    getAllTenants(): Observable<Tenant[]> {
        return this.http
            .get<Tenant[]>('/api/tenant')
            .pipe(
                map((tenants) =>
                    tenants.filter((tenant) =>
                        this.permissionService.isAllowedTenant(tenant.TenantPermissionGroup)
                    )
                )
            );
    }

    updateTenant(tenant: Tenant): void {
        this.http
            .put(`/api/tenant/${tenant.Value}`, tenant)
            .subscribe();
    }

    getAllProvisionedTenants(): Observable<Tenant[]> {
        return this.http
            .get<Tenant[]>('/api/tenant')
            .pipe(
                map((tenants) =>
                    tenants.filter((tenant) =>
                        this.permissionService.isAllowedTenant(tenant.TenantPermissionGroup) &&
                        tenant.ProvisionedApplications !== null && tenant.ProvisionedApplications[0] === 'airsweb'
                    )
                )
            );
    }

    getTenantsEnviromentByTenantId(tenantId: string): Observable<Tenant[]> {
        return this.http.get<Tenant[]>(`/api/tenant/environment/${tenantId}`);
    }

    getRecentApps(): Observable<ProvisionedApplicationViewModel[]> {
        return this.http
            .get<ProvisionedApplicationViewModel[]>('/api/tenant/recentlyprovisioned')
            .pipe(
                map((apps) =>
                    apps.filter((app) =>
                        this.permissionService.isAllowedTenant(app.TenantPermissionGroup)
                    )
                )
            );
    }

    getTenantList(view: string, page = 1, pageSize = 10): Observable<TenantListPageResult> {
        return this.http
            .get<TenantListPageResult>('/api/tenant/list', {
                params: { view, page: page.toString(), pageSize: pageSize.toString() }
            });
    }

    logTenantVisit(tenantId: string): Observable<void> {
        return this.http.post<void>(`/api/tenant/${tenantId}/visit`, {});
    }

    getAllMatrices(): Observable<NamedMatrixViewModel[]> {
        const url = `/api/proxy/ava/${this.selectedTenant.Value}/tenant/matrix`;
        return this.http.get<NamedMatrixViewModel[]>(url);
    }

    getMatrix(
        name: string,
        version: number | 'latest'
    ): Observable<NamedMatrixViewModel> {
        const url = `/api/proxy/ava/${this.selectedTenant.Value}/tenant/matrix/${name}/${version}`;
        return this.http.get<NamedMatrixViewModel>(url);
    }

    updateMigration(
        tenant: Tenant,
        moduleId: string,
        version: number,
        body: any
    ) {
        const url = `/api/proxy/ava/${tenant.Value}/formflow/migration/${moduleId}/${version}`;
        return this.http.post<{}>(url, body);
    }

    simulateMigration(
        tenant: Tenant,
        moduleId: string,
        version: number,
        body: any
    ) {
        const url = `/api/proxy/ava/${tenant.Value}/formflow/migration/${moduleId}/${version}/simulate`;
        return this.http.post<{}>(url, body);
    }

    getMigrations(tenant: Tenant, moduleId: string, continuationToken: string) {
        const url = `/api/proxy/ava/${tenant.Value}/formflow/migration/${moduleId}${continuationToken ?
            `?continuationToken=${continuationToken}` : ''
            }`;
        return this.http.get<IContinuationResponse<MigrationDto[]>>(url);
    }

    deleteMigration(tenant: Tenant, moduleId: string, version: number) {
        const url = `/api/proxy/ava/${tenant.Value}/formflow/migration/${moduleId}/${version}`;
        return this.http.delete<{}>(url);
    }

    saveMatrix(name: string, matrix: MatrixViewModel): Observable<any> {
        const url = `/api/proxy/ava/${this.selectedTenant.Value}/tenant/matrix/${name}`;
        return this.http.put(url, matrix);
    }

    getQuery(moduleId: string, queryId: string): Observable<QueryViewModel> {
        const url = `/api/udq/${this.selectedTenant.Value}/${moduleId}/${queryId}`;
        return this.http.get<QueryViewModel>(url);
    }

    updateQuery(
        queryViewModel: QueryViewModel,
        moduleId: string,
        queryId: string
    ): Observable<any> {
        const url = `/api/udq/${this.selectedTenant.Value}/${moduleId}/${queryId}`;
        return this.http.put(url, queryViewModel);
    }

    getQueryColumns(
        moduleId: string,
        queryId: string
    ): Promise<IModuleQueryDetailsModel> {
        return this.http
            .get<any>(
                `/api/udq/${this.selectedTenant.Value}/${moduleId}/${queryId}/columns`
            )
            .toPromise();
    }

    pushTranslations(translations: any) {
        const url = `/api/airsweblanguage/${this.selectedTenant.Value}/push-translations/en-gb`;
        return this.http.post(url, translations);
    }

    create(tenant: CreateTenantDto): Observable<CreateTenantJobDto> {
        return this.http.post<CreateTenantJobDto>('/api/tenant', tenant);
    }

    delete(tenantName: string) {
        return this.http.delete(`/api/tenant/${tenantName}`);
    }

    access(tenant: Tenant) {
        return this.http.post('/api/tenant/access', tenant);
    }

    createGuestUserIfNotExists(tenant: string) {
        return this.http.post(`/api/tenant/createguestuser/${tenant}`, '');
    }

    checkTenantExists(value: string): Observable<boolean> {
        return this.http.get(`/api/tenant/${value}`, { observe: 'response' }).pipe(
            map((response) => {
                if (response.status === HttpStatusCode.Ok) {
                    return true;
                } else if (response.status === HttpStatusCode.NoContent) {
                    return false;
                }
            })
        );
    }

    searchPassportUsers(val: string) {
        return this.http.get<any[]>(
            `/api/passport/users/search/${this.selectedTenant.Value}/?searchTerm=${val}`
        );
    }

    getListItems(vals: string[]) {
        return this.http.post<any[]>(
            `/api/airsweblist/${this.selectedTenant.Value}`,
            {
                QueryParameters: {
                    ListType: vals[0],
                    ListAlias: vals[1],
                    IncludeDeactivated: vals.length > 2 ? vals[2] : false
                },
            }
        );
    }

    getListAttributeDefinitions(listAlias: string): Observable<any[]> {
        return this.http.get<any[]>(
            `/api/airsweblist/${this.selectedTenant.Value}/${listAlias}/attributes?translateLabels=true`
        );
    }

    getAllLists(tenant: string): Observable<IMobileListItem[]> {
        return this.http.get<IMobileListItem[]>(`/api/tenant/lists/${tenant}`);
    }

    addListItem(
        listType: string,
        alias: string,
        newItemString: string
    ): Observable<ListItemViewModel> {
        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        const options = { headers };

        return this.http
            .post<ListItemViewModel>(
                `/api/airsweblist/${this.selectedTenant.Value}/add/${listType}/${alias}`,
                JSON.stringify(newItemString),
                options
            )
            .pipe(tap(() => this.updatedListItem$.next({ added: newItemString })));
    }

    getUpdatedListItem$(): Observable<any> {
        return this.updatedListItem$.asObservable();
    }

    addListAttribute(
        listAlias: string,
        newListAttribute: INewAttribute
    ): Observable<any> {
        return this.http.post<any>(
            `/api/airsweblist/${this.selectedTenant.Value}/${listAlias}/attributes`,
            newListAttribute,
            { headers: { 'Content-Type': 'application/json' } }
        );
    }

    resetCache(tenant: string): Observable<any> {
        return this.http.delete(`/api/tenant/cache/config/${tenant}`);
    }

    getAllTenantModules(tenant: string): Observable<AvaModule[]> {
        return this.http.get<AvaModule[]>(`/api/modules?clientId=${tenant}`);
    }

    getRecordFeedModules(tenant: Tenant): Observable<string[]> {
        return this.http.get<string[]>(
            `/api/modules/feedConfiguration?clientId=${tenant.Value}`
        );
    }

    getAllowedModules(tenant: string): Observable<string[]> {
        return this.http.get<string[]>(`/api/allowedmodules?clientId=${tenant}`);
    }

    getGroupableFields(moduleUniqueId: string): Observable<any> {
        return this.http.get<any>(`/api/proxy/ava/${this.selectedTenant.Value}/analytics/udq/${moduleUniqueId}/query/groupablefields`)
    }

    setAllowedModules(tenant: string, modules: string[]): Observable<any> {
        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        const options = { headers };
        return this.http.put<any[]>(
            `/api/allowedmodules?clientId=${tenant}`,
            modules,
            options
        );
    }

    getAddedList$(): Observable<any> {
        return this.addedList$.asObservable();
    }

    addList(newListString: string) {
        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        const options = { headers };
        newListString = newListString.trim();
        return this.http
            .post<ListViewModel>(
                `/api/airsweblist/${this.selectedTenant.Value}/addList/${encodeURIComponent(newListString)}`,
                JSON.stringify(newListString),
                options
            )
            .pipe(tap((newList) => this.addedList$.next(newList?.Key)));
    }

    deleteListItem(listType: string, alias: string, itemAlias: string) {
        return this.http
            .delete(
                `/api/airsweblist/${this.selectedTenant.Value
                }/delete/${listType}/${alias}?itemAlias=${encodeURIComponent(
                    itemAlias
                )}`
            )
            .pipe(tap(() => this.updatedListItem$.next({ delete: itemAlias })));
    }

    deleteListItems(listType: string, alias: string, itemAliases: string[]) {
        return this.http
            .put(
                `/api/airsweblist/${this.selectedTenant.Value
                }/delete/${listType}/${alias}`, itemAliases
            )
            .pipe(tap(() => this.updatedListItem$.next({ delete: itemAliases })));
    }

    deleteList(listType: string, alias: string) {
        return this.http
            .delete(
                `/api/airsweblist/${this.selectedTenant.Value
                }/${listType}/${alias}`
            );
    }

    getList(alias: string): Observable<ListViewModel> {
        return this.getLists().pipe(
            map((lists) => lists.find((list) => list.Key === alias))
        );
    }

    getLists() {
        return this.http.get<ListViewModel[]>(
            `/api/airsweblist/${this.selectedTenant.Value}/lists`
        );
    }

    updateListSelfServiceEnabled(
        listKey: string,
        enabled: boolean
    ): Observable<void> {
        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        const options = { headers };
        return this.http.post<void>(
            `/api/airsweblist/${this.selectedTenant.Value}/${listKey}/update-can-add`,
            JSON.stringify(enabled),
            options
        );
    }

    async loadStateRules(): Promise<any> {
        try {
            const response = await this.http
                .get<IRule[]>(`/api/rule/${this.selectedTenant.Value}/state`)
                .toPromise();
            this.stateRules = response;
        } catch (e) {
            await this.handleError(e);
        }
    }

    validateStateRules(stateRulesToValidate: number[]): string[] {
        const validationErrors: string[] = [];

        for (const stateRule of this.stateRules.filter(
            (rule) => stateRulesToValidate.indexOf(rule.Id) !== -1
        )) {
            if (stateRule.Id === null || stateRule.Id === undefined) {
                validationErrors.push('Rule Id must not be blank');
            }
            if (!stateRule.Description || stateRule.Description.length === 0) {
                validationErrors.push('Rule Description must not be blank');
            }
            if (!stateRule.OperationType || stateRule.OperationType.length === 0) {
                validationErrors.push('Rule Operation Type must not be blank');
            }
            if (!stateRule.Conditions || stateRule.Conditions.length === 0) {
                validationErrors.push('Rule must have at least one condition');
            }
            if (
                stateRule.InstructionType === null ||
                stateRule.InstructionType === undefined
            ) {
                validationErrors.push('Rule Instruction Type must not be blank');
            }
            if (stateRule.IsActive === null || stateRule.IsActive === undefined) {
                validationErrors.push('Rule Is Active must not be blank');
            }
            if (stateRule.IsValid === null || stateRule.IsValid === undefined) {
                validationErrors.push('Rule Is Valid must not be blank');
            }

            for (const condition of stateRule.Conditions) {
                if (condition.ValueArray && condition.ValueArray.length > 0) {
                    condition.Value = condition.ValueArray.join();
                }

                if (!condition.$type || condition.$type.length === 0) {
                    validationErrors.push('Rule Condition Type must not be blank');
                }
                if (!condition.ParentName || condition.ParentName.length === 0) {
                    validationErrors.push('Rule Condition Parent Name must not be blank');
                }
                if (!condition.PropertyName || condition.PropertyName.length === 0) {
                    validationErrors.push('Rule Condition Field must not be blank');
                }
                if (!condition.PropertyType || condition.PropertyType.length === 0) {
                    validationErrors.push('Rule Condition Field Type must not be blank');
                }
                if (condition.Operator === null || condition.Operator === undefined) {
                    validationErrors.push('Rule Condition Operator must not be blank');
                }
                if (!condition.Value || condition.Value.length === 0) {
                    validationErrors.push('Rule Condition Value must not be blank');
                }
            }
        }

        return validationErrors;
    }

    saveStateRules(moduleId: string, stateRulesToSave: number[]) {
        const rules = this.stateRules.filter(
            (rule) => stateRulesToSave.indexOf(rule.Id) !== -1
        );

        return this.http.post(
            `/api/rule/${this.selectedTenant.Value}/state/${moduleId}`,
            rules
        );
    }

    async getEmailRules(): Promise<any> {
        try {
            const response = await this.http
                .get<IRule[]>(`/api/rule/${this.selectedTenant.Value}/email`)
                .toPromise();
            this.emailRules = response;
        } catch (e) {
            await this.handleError(e);
        }
    }

    saveEmailRules(moduleId: string, rules: IRule[]) {
        return this.http.post(
            `/api/rule/${this.selectedTenant.Value}/email/${moduleId}`,
            rules
        );
    }

    public async getNextRuleId(): Promise<number> {
        try {
            const response = await this.http
                .get<number>(`/api/rule/${this.selectedTenant.Value}/getnextruleid`)
                .toPromise();
            return response;
        } catch (e) {
            return await this.handleError(e);
        }
    }

    public copyConfigExportImport(
        exportRequest: CopyConfigExportImportRequest
    ): Observable<object> {
        const url = '/api/tenant/copyconfig/export-import';
        return this.http.post(url, exportRequest);
    }

    public copyConfigExportOnly(
        exportRequest: CopyConfigExportOnlyRequest
    ): Observable<object> {
        const url = '/api/tenant/copyconfig/export';
        return this.http.post(url, exportRequest);
    }

    public copyConfigImportOnly(
        exportRequest: CopyConfigImportOnlyRequest
    ): Observable<object> {
        const url = '/api/tenant/copyconfig/import';
        return this.http.post(url, exportRequest);
    }    

    public async addSubFeaturesToGraph(tenant: Tenant): Promise<boolean> {
        try {
            const response = await this.http
                .post('/api/tenant/addsubfeaturestograph', tenant)
                .toPromise();
            return response === true;
        } catch (e) {
            return await this.handleError(e);
        }
    }

    getTenantTypes(): Observable<TenantProperty[]> {
        return this.http.get<TenantProperty[]>('/api/tenanttypes');
    }

    async updateTenantType(clientId: string, type: string): Promise<any> {
        await this.http
            .put(`/api/tenanttypes/${clientId}/${type}`, {})
            .toPromise()
            .then(() => {
                if (this.selectedTenant.ProvisionedApplications.includes(ProvisionedApplicationsTypes.Airsweb)) {
                    this.resetCache(clientId).subscribe();
                }
                return this.tenantType.next(type);
            })
            .catch((e) => this.handleError(e));
    }

    public getEnvironmentDetails(clientId: string): void {
        this.http
            .get<TenantEnvironmentDetails>(
                `/api/tenantenvironmentdetails/${clientId}`
            )
            .subscribe((data: TenantEnvironmentDetails) => {
                this.setEnvironmentDetails(data);
            });
    }

    public setEnvironmentDetails(environmentDetails: TenantEnvironmentDetails) {
        this.tenantEnvironmentDetails.next(environmentDetails);
    }

    public currentEnvironmentDetails(): Observable<TenantEnvironmentDetails> {
        return this.tenantEnvironmentDetails.asObservable();
    }

    public getEnvironmentDetailsByTenant(
        tenantName: string
    ): Observable<TenantEnvironmentDetails> {
        return this.http.get<TenantEnvironmentDetails>(
            `/api/tenantenvironmentdetails/${tenantName}`
        );
    }

    getTenantStatus(clientId: string): void {
        this.http
            .get<string>(`/api/tenantstatuses/${clientId}`)
            .subscribe((data: string) => {
                if (data == null) {
                    data = 'None';
                }
                this.setTenantStatus(data);
            });
    }

    getTenantStatuses(): Observable<TenantProperty[]> {
        return this.http.get<TenantProperty[]>('/api/tenantstatuses');
    }

    getPilotTenantStatuses(): Observable<string[]> {
        return this.http.get<string[]>('/api/tenant/pilot-tenant-statuses');
    }

    updatePilotTenantStatuses(statuses: string[]): Observable<void> {
        return this.http.put<void>('/api/tenant/pilot-tenant-statuses', { Statuses: statuses });
    }

    async updateTenantStatus(clientId: string, status: string): Promise<any> {
        await this.http
            .put(`/api/tenantstatuses/${clientId}/${status}`, {})
            .toPromise()
            .then(() => {
                if (this.selectedTenant.ProvisionedApplications.includes(ProvisionedApplicationsTypes.Airsweb)) {
                    this.resetCache(clientId).subscribe();
                }
                return this.tenantStatus.next(status);
            })
            .catch((e) => this.handleError(e));
    }

    getCurrentTenantProvisionJob(): Observable<string> {
        const clientId = this.selectedTenant.Value;
        return this.http.get<string>(`/api/tenant/${clientId}/provision-job`);
    }

    public setTenantStatus(tenantStatus: string) {
        this.tenantStatus.next(tenantStatus);
    }

    public currentTenantStatus(): Observable<string> {
        return this.tenantStatus.asObservable();
    }

    public currentOrganisationId(): Observable<string | null> {
        return this.organisationId.asObservable();
    }

    public getTenantType(clientId: string): void {
        this.http
            .get<string>(`/api/tenanttypes/${clientId}`)
            .subscribe((data: string) => {
                this.setTenantType(data);
            });
    }

    public setTenantType(tenantType: string) {
        this.tenantType.next(tenantType);
    }

    public currentTenantType(): Observable<string> {
        return this.tenantType.asObservable();
    }

    public getTenantOrganisationId(): void {
        const clientId = this.selectedTenant.Value;
        this.http.get<string>(`/api/tenant-organisation/${clientId}`)
            .subscribe((data: string) => {
                if (this.selectedTenant?.Value !== clientId) {
                    return;
                }

                this.setTenantOrganisationId(data);
            });
    }

    public setTenantOrganisationId(organisationId: string) {
        this.organisationId.next(organisationId);
    }

    public currentTenantOrganisationId(): Observable<string | null> {
        return this.organisationId.asObservable();
    }

    public getTenantGroups(): Observable<TenantGroupDto[]> {
        return this.http.get<TenantGroupDto[]>('/api/tenantgroups');
    }

    public getTenantPermissionGroups(): Observable<TenantGroupPermissionDto[]> {
        return this.http.get<TenantGroupPermissionDto[]>(
            '/api/tenantgroups/tenantgroupspermissions'
        );
    }

    public setTenantGroupPermission(
        tenantGroup: TenantGroupPermission
    ): Observable<void> {
        const tenantGroupDto = new TenantGroupPermissionDto(tenantGroup);
        return this.http.put<void>(
            `/api/tenantgroups/tenantgroupspermissions/${tenantGroup.tenantGroupName}`,
            tenantGroupDto
        );
    }

    async updateTenantPermissionGroup(
        clientId: string,
        permissionGroup: string
    ): Promise<any> {
        await this.http
            .put(`/api/tenantpermissiongroup/${clientId}/${permissionGroup}`, {})
            .toPromise()
            .then(() => {
                if (this.selectedTenant.ProvisionedApplications.includes(ProvisionedApplicationsTypes.Airsweb)) {
                    this.resetCache(clientId).subscribe();
                }
                return this.tenantPermissionGroup.next(permissionGroup);
            })
            .catch((e) => this.handleError(e));
    }

    public getTenantPermissionGroup(clientId: string): void {
        this.http
            .get<string>(`/api/tenantpermissiongroup/${clientId}`)
            .subscribe((data: string) => {
                if (data == null) {
                    data = 'None';
                }
                this.setTenantPermissionGroup(data);
            });
    }

    public setTenantPermissionGroup(tenantPermissionGroup: string) {
        this.tenantPermissionGroup.next(tenantPermissionGroup);
    }

    public currentTenantPermissionGroup(): Observable<string> {
        return this.tenantPermissionGroup.asObservable();
    }

    updateFeatureFlag(name: string, featureFlag: FeatureFlag): Observable<{}> {
        const url = `/api/feature-flags/${this.selectedTenant.Value}/${name}`;
        return this.http.put(url, featureFlag);
    }

    getFeatureFlags(): Observable<FeatureFlag[]> {
        const url = `/api/feature-flags/${this.selectedTenant.Value}`;
        return this.http.get<FeatureFlag[]>(url);
    }

    clearMobileCache() {
        const url = `/api/feature-flags/mobilecaches/${this.selectedTenant.Value}`;
        return this.http.put(url, {});
    }

    public setEcoTranslateSynchronisation(ecoTranslateSynchronisation: boolean) {
        this.ecoTranslateSynchronisation.next(ecoTranslateSynchronisation);
    }

    getEcoTranslateSynchronisation(clientId: string): void {
        this.http
            .get<boolean>(
                `/api/tenant/config/allowecotranslatesynchronisation/${clientId}`
            )
            .subscribe((data: boolean) => {
                if (data == null) {
                    data = false;
                }
                this.setEcoTranslateSynchronisation(data);
            });
    }

    async updateEcoTranslateSynchronisation(
        clientId: string,
        ecoTranslateSynchronisation: boolean
    ): Promise<any> {
        await this.http
            .put(
                `/api/tenant/config/allowecotranslatesynchronisation/${clientId}/${ecoTranslateSynchronisation}`,
                {}
            )
            .toPromise()
            .then(() => {
                if (this.selectedTenant.ProvisionedApplications.includes(ProvisionedApplicationsTypes.Airsweb)) {
                    this.resetCache(clientId).subscribe();
                }
                return this.ecoTranslateSynchronisation.next(
                    ecoTranslateSynchronisation
                );
            })
            .catch((e) => this.handleError(e));
    }

    public currentEcoTranslateSynchronisation(): Observable<boolean> {
        return this.ecoTranslateSynchronisation.asObservable();
    }

    cloneTenant(
        tenant: NewCloneTenantDto, jobId: string
    ): Observable<void> {
        return this.http.post<void>(
            `/api/tenant/clone/${jobId}/start-job`,
            tenant
        );
    }

    cloneTenantExportImport(
        cloneRequest: NewCloneTenantDto
    ): Observable<string> {
        const url = '/api/tenant/clone/export-import';
        return this.http.post<string>(url, cloneRequest);
    }

    cloneTenantFreeTrial(
        freeTrialRequest: NewFreeTrialTenantDto
    ): Observable<string> {
        const url = '/api/tenant/clone/free-trial';
        return this.http.post<string>(url, freeTrialRequest);
    }

    cloneTenantExportOnly(
        cloneRequest: NewCloneTenantDto
    ): Observable<string> {
        const url = '/api/tenant/clone/export';
        return this.http.post<string>(url, cloneRequest);
    }

    cloneTenantImportOnly(
        cloneRequest: NewCloneTenantDto
    ): Observable<string> {
        const url = '/api/tenant/clone/import';
        return this.http.post<string>(url, cloneRequest);
    }

    getCloneJobAndTenantId(): Observable<CreateTenantJobDto> {
        return this.http.post<CreateTenantJobDto>('api/tenant/clone/create-job', {});
    }

    getTenantIndustries(): Observable<string[]> {
        return this.http.get<string[]>('/api/tenant/attributes/industries');
    }

    getTenantSizes(): Observable<string[]> {
        return this.http.get<string[]>('/api/tenant/attributes/sizes');
    }

    getTenantAttributes(clientId: string): Observable<TenantAttributes> {
        const url = `/api/tenant/${clientId}/attributes`;
        return this.http.get<TenantAttributes>(url);
    }

    updateTenantAttributes(tenant: Tenant, attributes: TenantAttributes): Promise<any> {
        const url = `/api/tenant/${tenant.Value}/attributes`;
        return this.http.post(url, attributes).toPromise();
    }

    private handleError(error: any): Promise<any> {
        return Promise.reject(error.message || error);
    }
}
